// /kitchen-sink — the Field Notes review gate, in the real app.
//
// Every ported component in every state, both themes, one URL. Mounted
// OUTSIDE AuthWrapper (purely presentational, zero data) so automated
// Playwright sweeps reach it unauthenticated. The rule from the gap
// inventory: if a component/state isn't in the sink, it doesn't exist.
import React from 'react';
import {
  MASTERY_LEVELS,
  MasteryGlyph,
  MasteryChip,
  DueMarker,
  BlockerMarker,
  BlockerNote,
  StreakPill,
} from './ui/mastery';
import { DistributionBar, ConceptRow, ConceptCell, CourseCard, DomainCard } from './ui/dashboard';
import { ConfirmProvider, useConfirm } from './ui/ConfirmDialog';

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="turn-eyebrow !text-[11px]">{title}</h2>
      <div className="card p-5 space-y-4">{children}</div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint w-36 flex-none">{label}</span>
      {children}
    </div>
  );
}

const OPT_STATES = [undefined, 'selected', 'correct', 'wrong', 'muted'];

function KitchenSinkInner() {
  const [theme, setTheme] = React.useState('light');
  const [motion, setMotion] = React.useState('full');

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    return () => document.documentElement.removeAttribute('data-theme');
  }, [theme]);
  React.useEffect(() => {
    if (motion === 'reduced') document.documentElement.setAttribute('data-motion', 'reduced');
    else document.documentElement.removeAttribute('data-motion');
    return () => document.documentElement.removeAttribute('data-motion');
  }, [motion]);

  return (
    <div className="min-h-screen bg-paper text-ink px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <header className="mb-8 flex items-end gap-4">
          <div className="flex-1">
            <p className="turn-eyebrow">review gate</p>
            <h1 className="font-heading text-3xl">Kitchen Sink</h1>
            <p className="text-sm text-mutedink mt-1">
              Every Field Notes component, every state. If it isn’t here, it isn’t ported.
            </p>
          </div>
          <div className="flex gap-2">
            {['light', 'dark'].map((t) => (
              <button key={t} type="button" onClick={() => setTheme(t)}
                className={`fn-btn ${theme === t ? 'fn-btn--primary' : ''}`} data-testid={`theme-${t}`}>
                {t}
              </button>
            ))}
            <button type="button" onClick={() => setMotion(motion === 'full' ? 'reduced' : 'full')} className="fn-btn">
              {motion === 'full' ? 'motion: full' : 'motion: reduced'}
            </button>
          </div>
        </header>

        <Section title="typography">
          <h1 className="font-heading text-[40px] leading-[1.04]">Pod Networking is ready for review.</h1>
          <h2 className="font-heading text-xl">Fixing Access Modes unlocks Volume Types.</h2>
          <p className="max-w-[66ch] text-[14.5px] leading-[1.62] text-read">
            Your last two attempts both tripped on the same edge: a Pod can’t resolve a
            Service until the CNI plugin is healthy. That’s the thread we’ll pull. Body
            stays calm at measure — learning is reading.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-mutedink">mono label · the data voice</p>
          <p className="text-sm text-read">
            Inline <code>kubectl get pods</code> and a block:
          </p>
          <pre className="text-xs p-3 rounded-md" style={{ background: 'var(--code-block-bg)', color: 'var(--code-block-ink)' }}>
            {'apiVersion: v1\nkind: Service\nmetadata:\n  name: web'}
          </pre>
        </Section>

        <Section title="mastery vocabulary">
          <Row label="glyphs">
            {MASTERY_LEVELS.map((l) => (
              <span key={l} className="inline-flex items-center gap-1.5">
                <MasteryGlyph level={l} />
                <span className="font-mono text-[10px] text-mutedink">{l}</span>
              </span>
            ))}
          </Row>
          <Row label="sizes">
            <MasteryGlyph level="developing" size="sm" />
            <MasteryGlyph level="developing" />
            <MasteryGlyph level="developing" size="lg" />
          </Row>
          <Row label="chip">
            <MasteryChip level="struggling" />
            <MasteryChip level="mastered" />
          </Row>
          <Row label="markers">
            <DueMarker />
            <BlockerMarker />
            <StreakPill streak={{ current_streak: 6, longest_streak: 19 }} />
          </Row>
          <BlockerNote concept="Pod Networking" unlocks={['Service Discovery', 'Ingress']} />
          <BlockerNote concept="Routing" />
        </Section>

        <Section title="quiz options — all states">
          <div className="q-options">
            {OPT_STATES.map((st, i) => (
              <button key={i} type="button" className="q-opt" data-state={st} disabled={st === 'muted'}>
                <span className="q-key">{String.fromCharCode(65 + i)}</span>
                <span>{st ? `Option in ${st} state` : 'Default option — hover shows accent edge'}</span>
              </button>
            ))}
          </div>
          <div className="q-tested">
            <span className="q-tag">Pod Networking</span>
            <span className="q-tag">Service Discovery</span>
            <span className="q-tag">CoreDNS</span>
          </div>
          <div className="q-feedback">
            <div className="qf-top">
              <span className="qf-verdict" data-ok="true">Correct</span>
              <span className="qf-grade"><b>9</b>/10</span>
            </div>
            <p className="text-sm text-read">CoreDNS health is the first dependency in name resolution.</p>
          </div>
          <div className="q-feedback">
            <div className="qf-top">
              <span className="qf-verdict" data-ok="false">Not quite</span>
              <span className="qf-grade"><b>3</b>/10</span>
            </div>
            <p className="text-sm text-read">targetPort matters later — resolution comes first.</p>
          </div>
        </Section>

        <Section title="quiz card composition">
          <div className="qcard">
            <div className="qcard-head">
              <span className="qk">Quiz · 1 of 3</span>
              <span className="qd">medium</span>
            </div>
            <div className="qcard-body">
              <div className="q-prompt">A Pod can’t reach a Service by name. What’s the first thing to check?</div>
              <div className="q-options">
                <button type="button" className="q-opt"><span className="q-key">A</span><span>The Service’s targetPort value</span></button>
                <button type="button" className="q-opt" data-state="selected"><span className="q-key">B</span><span>CoreDNS / CNI plugin health</span></button>
              </div>
            </div>
            <div className="qcard-foot">
              <button type="button" className="skip">skip</button>
              <span className="spacer" />
              <button type="button" className="fn-btn fn-btn--primary">Submit</button>
            </div>
          </div>
        </Section>

        <Section title="tool cards">
          <div className="tcard">
            <div className="tcard-head">
              <span className="tc-kicker">Course proposal</span>
              <span className="tc-pill">draft</span>
            </div>
            <div className="tcard-body">
              <div className="pc-kicker">new course</div>
              <div className="pc-title">Terraform in Practice</div>
              <div className="pc-desc">A 5-domain path from state &amp; providers through modules and CI drift detection.</div>
              <div className="pc-domains">
                <div className="pc-dom"><b>Core &amp; State</b><span>Providers · State · Backends · Resource Graph</span></div>
                <div className="pc-dom"><b>Modules</b><span>Composition · Inputs/Outputs · Registry</span></div>
              </div>
              <div className="pc-why">You asked for hands-on Terraform — this maps to your stated goal.</div>
            </div>
            <div className="pc-actions">
              <button type="button" className="fn-btn fn-btn--primary">Add to my courses</button>
              <button type="button" className="fn-btn fn-btn--ghost">Discard</button>
            </div>
          </div>
          <div className="tcard">
            <div className="tcard-head"><span className="tc-kicker">Course proposal</span><span className="tc-pill">draft</span></div>
            <div className="dc-resolved">designing the curriculum…</div>
          </div>
          <div className="tcard">
            <div className="tcard-head"><span className="tc-kicker">Draft</span><span className="tc-pill">unsaved</span></div>
            <div className="dc-resolved saved">✓ saved to Pod Networking</div>
          </div>
          <div className="action-row !border !border-line rounded-xl">
            <button type="button" className="action-btn"><span className="ab-glyph">▶</span>Review the 3 due concepts<span className="arrow">→</span></button>
            <button type="button" className="action-btn"><span className="ab-glyph">↗</span>Tackle Pod Networking<span className="arrow">→</span></button>
          </div>
          <div className="error-note">
            <span className="en-mark">!</span>
            <span className="en-text"><b>Connection lost</b> before the tutor finished — retry the turn.</span>
            <button type="button" className="fn-btn">Retry</button>
          </div>
        </Section>

        <Section title="results composition">
          <div className="results-head">
            <div className="rh-eyebrow">quiz complete · pod networking</div>
            <div className="rh-title">You found <span className="hl">the edge</span>.</div>
            <div className="rh-sub">Two of three — and both misses share one root: DNS resolution order.</div>
          </div>
          <div className="score-ring">
            <div className="sr-num">7.3<small>/10</small></div>
            <div className="sr-meta">3 questions<br /><b>2 correct</b> · 1 to revisit</div>
          </div>
          <div className="result-q">
            <div className="rq-head">
              <span className="rq-icon" data-ok="true">✓</span>
              <div className="rq-body"><div className="rq-q">First thing to check when a Pod can’t reach a Service by name?</div></div>
              <span className="rq-grade"><b>9</b>/10</span>
            </div>
          </div>
          <div className="result-q">
            <div className="rq-head">
              <span className="rq-icon" data-ok="false">✕</span>
              <div className="rq-body"><div className="rq-q">Which field maps Service port → container port?</div></div>
              <span className="rq-grade"><b>3</b>/10</span>
            </div>
          </div>
          <div className="results-cta">
            <div className="rc-body">
              <div className="rc-eyebrow">observed blocker</div>
              <div className="rc-title">Want to clear <span className="hl">targetPort mapping</span> for good?</div>
              <div className="rc-sub">Your last two misses share it — the tutor can drill exactly this edge.</div>
            </div>
            <button type="button" className="fn-btn fn-btn--primary">Ask the tutor</button>
          </div>
        </Section>

        <Section title="dashboard surfaces">
          <Row label="distribution">
            <div className="w-64"><DistributionBar dist={{ struggling: 4, developing: 6, proficient: 8, mastered: 2 }} legend /></div>
          </Row>
          <DomainCard
            d={{
              key: 'networking', name: 'Networking', description: 'Services, DNS, ingress',
              dist: { struggling: 2, developing: 1, proficient: 1 }, due: 2,
              blockers: ['Pod Networking'],
              rows: [
                { concept: 'Pod Networking', mastery_level: 'struggling', mastery_score: 3.2, due_for_review: true },
                { concept: 'Service Discovery', mastery_level: 'developing', mastery_score: 4.8, due_for_review: true },
                { concept: 'Ingress', mastery_level: 'proficient', mastery_score: 6.9 },
              ],
            }}
            onAsk={() => {}}
          />
          <div className="cgrid">
            <ConceptCell m={{ concept: 'Route Tables', mastery_level: 'struggling', mastery_score: 3.1, is_blocker: true, due_for_review: true }} onAsk={() => {}} />
            <ConceptCell m={{ concept: 'VPC', mastery_level: 'mastered', mastery_score: 9.4 }} onAsk={() => {}} />
            <ConceptCell m={{ concept: 'Subnets', mastery_level: 'advanced', mastery_score: 8.3 }} onAsk={() => {}} />
          </div>
          <div className="lib-grid">
            <CourseCard c={{ code: 'CKA', name: 'Certified Kubernetes Administrator', description: 'Exam-prep across 4 domains.', status: 'active', concepts: 26, due: 20, blockers: 2, dist: { struggling: 4, developing: 6, proficient: 8, mastered: 2 }, last_active: '2w ago' }} onOpen={() => {}} />
            <CourseCard c={{ code: 'SPAN-B1', name: 'Spanish · B1', description: 'Conversation-first track.', status: 'fresh', concepts: 18 }} onOpen={() => {}} />
          </div>
        </Section>

        <Section title="chat cards (7/8/10)">
          <div className="tcard" role="group" aria-label="curriculum edit demo">
            <div className="tcard-head">
              <span className="tc-kicker">Curriculum edit</span>
              <span className="tc-pill">draft</span>
            </div>
            <div className="tcard-body">
              <div className="pc-kicker">CKA · storage</div>
              <p className="text-sm text-read">Add concept <strong>Access Modes</strong> to domain <code className="text-xs">storage</code>.</p>
              <div className="pc-why">You've hit this gap in three quizzes; it blocks PVCs.</div>
            </div>
            <div className="pc-actions">
              <button type="button" className="fn-btn fn-btn--primary">Apply edit</button>
              <button type="button" className="fn-btn fn-btn--ghost">Discard</button>
            </div>
          </div>
          <div className="tcard" role="group" aria-label="suggested action demo">
            <div className="tcard-head"><span className="tc-kicker">Where to next</span></div>
            <div className="action-row" style={{ borderTop: 0 }}>
              <button type="button" className="action-btn">
                <span className="ab-glyph">▶</span>
                <span>Practice now<span style={{ color: 'var(--muted)' }}> · Access Modes</span></span>
                <span className="arrow">→</span>
              </button>
            </div>
          </div>
          <div className="tcard" role="group" aria-label="drafted note demo">
            <div className="tcard-head">
              <span className="tc-kicker">Drafted note</span>
              <span className="tc-pill">not saved</span>
            </div>
            <div className="tcard-body">
              <div className="dc-title">Access Modes</div>
              <pre className="dc-body text-xs whitespace-pre-wrap">RWO mounts to ONE node — pods on the same node share it...</pre>
            </div>
            <div className="dc-resolved saved">✓ saved — the notes for Access Modes are live</div>
          </div>
        </Section>

        <Section title="confirm dialog">
          <ConfirmDemo />
        </Section>

        <Section title="buttons">
          <Row label="fn-btn">
            <button type="button" className="fn-btn fn-btn--primary">Primary</button>
            <button type="button" className="fn-btn">Default</button>
            <button type="button" className="fn-btn fn-btn--ghost">Ghost</button>
            <button type="button" className="fn-btn" disabled>Disabled</button>
          </Row>
          <Row label="legacy .btn">
            <button type="button" className="btn btn-primary">btn-primary</button>
            <button type="button" className="btn btn-secondary">btn-secondary</button>
          </Row>
        </Section>
      </div>
    </div>
  );
}


function ConfirmDemo() {
  const confirm = useConfirm();
  const [last, setLast] = React.useState('—');
  return (
    <div className="flex items-center gap-3">
      <button type="button" className="fn-btn" onClick={() =>
        confirm({ title: 'Start a fresh conversation?', body: 'The current thread will be archived.', confirmLabel: 'Start fresh' }).then((ok) => setLast(String(ok)))
      }>neutral</button>
      <button type="button" className="fn-btn" onClick={() =>
        confirm({ title: 'Delete “CKA Prep”?', body: 'This archives the course — history preserved.', confirmLabel: 'Delete course', danger: true }).then((ok) => setLast(String(ok)))
      }>danger</button>
      <span className="font-mono text-[10px] text-mutedink">last: {last}</span>
    </div>
  );
}

export default function KitchenSink() {
  return (
    <ConfirmProvider>
      <KitchenSinkInner />
    </ConfirmProvider>
  );
}
